import React from 'react';
import { Box, CheckBox, Inscription, List, Preloader, Text, WarningCard, WarningIcon } from '@common-fe/common-fe';

import AppButton from '@/components/controls/AppButton';

import { CardPurseAccess } from '../ManageAccountAccessModal.types';

import { AccountAccessListItem } from './AccountAccessListItem';

interface Props {
  purses: CardPurseAccess[];
  lockedIds: string[];
  editableLockedIds: string[];
  employeeUnlockForbiddenIds: string[];
  isAllEmployeeUnlockForbidden: boolean;
  isLoading?: boolean;
  onToggle: (id: string, locked: boolean) => void;
  onSetEmployeeUnlockForbidden: (id: string, value: boolean) => void;
  onSetAllEmployeeUnlockForbidden: (value: boolean) => void;
  onCancel: () => void;
  onNext: () => void;
}

export const ManageAccountAccessForm: React.FC<Props> = ({
  purses,
  lockedIds,
  editableLockedIds,
  employeeUnlockForbiddenIds,
  isAllEmployeeUnlockForbidden,
  isLoading,
  onToggle,
  onSetEmployeeUnlockForbidden,
  onSetAllEmployeeUnlockForbidden,
  onCancel,
  onNext,
}) => (
  <Box direction="column" data-testid="manage-account-access-form">
    <Box round="moduleRound" pad="spacing24" background="module">
      <Text color="textBody" weight="bold" margin={{ bottom: 'spacing16' }}>{`Accounts: ${purses.length}`}</Text>
      <List>
        <List.Header pad={{ vertical: 'spacing6', horizontal: 'spacing24' }}>
          <Box style={{ width: '269px' }}>
            <Text color="textBody" size="small" weight="bold">Accounts</Text>
          </Box>
          <Text color="textBody" size="small" weight="bold">Access</Text>
          <Box direction="row" align="center" gap="spacing8" margin={{ left: 'auto' }}>
            <Text color="textBody" size="small" weight="bold">Prevent employee edit</Text>
            <CheckBox
              name="preventEmployeeEditAll"
              role="checkbox"
              aria-label="preventEmployeeEditAll"
              checked={isAllEmployeeUnlockForbidden}
              disabled={editableLockedIds.length === 0}
              onChange={({ target }) => onSetAllEmployeeUnlockForbidden(target.checked)}
            />
          </Box>
        </List.Header>
      </List>
      {purses.map((purse) => (
        <AccountAccessListItem
          key={purse.id}
          purse={purse}
          isLocked={lockedIds.includes(purse.id)}
          employeeUnlockForbidden={employeeUnlockForbiddenIds.includes(purse.id)}
          onToggle={onToggle}
          onSetEmployeeUnlockForbidden={onSetEmployeeUnlockForbidden}
        />
      ))}
      <Box margin={{ top: 'spacing16' }}>
        <WarningCard
          icon={<WarningIcon />}
          title={(
            <Inscription weight="bold" lineHeight="20px" color="textBody">
              The account(s) selected will be locked for the primary card as well as any dependent cards.
            </Inscription>
          )}
          topBorderColor="transparent"
          containerProps={{ height: '72px', pad: { horizontal: 'spacing24', vertical: 'spacing16' }, round: 'container2Round' }}
        />
      </Box>
    </Box>
    <Box
      direction="row"
      fill="horizontal"
      margin={{ top: 'spacing24' }}
      align="center"
      justify="end"
      gap="spacing8"
    >
      <AppButton
        buttonType="secondary"
        width="140px"
        type="button"
        testId="manage-account-access-form_cancel-button"
        onClick={onCancel}
      >
        Cancel
      </AppButton>
      <AppButton
        disabled={isLoading}
        type="button"
        width="140px"
        testId="manage-account-access-form_save-button"
        onClick={onNext}
      >
        {isLoading ? <Preloader color="white" /> : 'Save'}
      </AppButton>
    </Box>
  </Box>
);
